import * as React from 'react';
import { PropsWithChildren, ReactElement, ReactNode } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import DialogButton, { DialogButtonProps } from './Button';
import DialogDescription, { DialogDescriptionProps } from './Description';
import Modal from './Modal';
import PlatformColor from './PlatformColor';
import DialogTitle, { DialogTitleProps } from './Title';
import useDialogTheme, { StyleBuilder } from './useTheme';

type TitleElement = ReactElement<DialogTitleProps, typeof DialogTitle>;
type DescriptionElement = ReactElement<
  DialogDescriptionProps,
  typeof DialogDescription
>;
type ButtonElement = ReactElement<DialogButtonProps, typeof DialogButton>;

const iOS = Platform.OS === 'ios';

export type DialogContainerProps = PropsWithChildren<{
  blurComponentIOS?: ReactNode;
  buttonSeparatorStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  footerStyle?: StyleProp<ViewStyle>;
  headerStyle?: StyleProp<ViewStyle>;
  blurStyle?: StyleProp<ViewStyle>;
  visible?: boolean;
  verticalButtons?: boolean;
  onBackdropPress?: () => void;
  onRequestClose?: () => void;
  onDismiss?: () => void;
  keyboardVerticalOffset?: number;
  useNativeDriver?: boolean;
}>;

const DialogContainer: React.FC<DialogContainerProps> = props => {
  const {
    blurComponentIOS,
    buttonSeparatorStyle,
    children,
    contentStyle,
    footerStyle,
    headerStyle,
    blurStyle,
    visible = false,
    verticalButtons = false,
    keyboardVerticalOffset = 40,
    ...nodeProps
  } = props;
  const titleChildrens: TitleElement[] = [];
  const descriptionChildrens: DescriptionElement[] = [];
  const buttonChildrens: (ButtonElement | JSX.Element)[] = [];
  const otherChildrens: ReactNode[] = [];
  const { styles } = useDialogTheme(buildStyles);
  React.Children.forEach(children, child => {
    if (typeof child === 'object' && child !== null && 'type' in child) {
      // @ts-expect-error: "Property 'displayName' does not exist on type 'string"
      const displayName = child.type?.displayName || child.type?.name;
      switch (displayName) {
        case DialogTitle.displayName:
          titleChildrens.push(child as TitleElement);
          return;
        case DialogDescription.displayName:
          descriptionChildrens.push(child as DescriptionElement);
          return;
        case DialogButton.displayName:
          if (Platform.OS !== 'android' && buttonChildrens.length > 0) {
            buttonChildrens.push(
              <View
                style={[
                  verticalButtons
                    ? styles.buttonSeparatorVertical
                    : styles.buttonSeparatorHorizontal,
                  buttonSeparatorStyle,
                ]}
              />,
            );
          }
          buttonChildrens.push(child as ButtonElement);
          return;
      }
    }
    otherChildrens.push(child);
  });
  return (
    <Modal
      renderToHardwareTextureAndroid={true}
      transparent={true}
      visible={visible}
      {...nodeProps}
    >
      <KeyboardAvoidingView
        behavior={iOS ? 'padding' : undefined}
        keyboardVerticalOffset={iOS ? keyboardVerticalOffset : undefined}
        style={styles.centeredView}
      >
        <View style={[styles.content, contentStyle]}>
          {Platform.OS === 'ios' && blurComponentIOS}
          {Platform.OS === 'ios' && !blurComponentIOS && (
            <View style={[styles.blur, blurStyle]} />
          )}
          <View style={[styles.header, headerStyle]}>
            {titleChildrens}
            {descriptionChildrens}
          </View>
          {otherChildrens}
          {Boolean(buttonChildrens.length) && (
            <>
              {Platform.OS !== 'android' && (
                <View style={styles.buttonSeparatorVertical} />
              )}
              <View
                style={[
                  styles.footer,
                  verticalButtons ? styles.footerVertical : null,
                  footerStyle,
                ]}
              >
                {buttonChildrens.map((x, i) =>
                  React.cloneElement(x, {
                    key: `dialog-button-${i}`,
                  }),
                )}
              </View>
            </>
          )}
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const buildStyles: StyleBuilder = () =>
  StyleSheet.create({
    centeredView: {
      marginTop: 22,
    },
    blur: {
      position: 'absolute',
      backgroundColor: PlatformColor('systemGray6'), // "rgb(255,255,255)",
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    },
    content: Platform.select({
      default: {
        width: 270,
        backgroundColor: 'white',
        flexDirection: 'column',
        borderRadius: 13,
        overflow: 'hidden',
      },
      ios: {
        width: 270,
        flexDirection: 'column',
        borderRadius: 13,
        overflow: 'hidden',
      },
      android: {
        backgroundColor: PlatformColor('?attr/colorBackgroundFloating'),
        flexDirection: 'column',
        borderRadius: 3,
        padding: 16,
        margin: 16,
        overflow: 'hidden',
        elevation: 4,
        minWidth: 300,
      },
    }),
    header: Platform.select({
      default: {
        margin: 18,
      },
      android: {
        margin: 12,
      },
    }),
    footer: {
      flexDirection: 'row',
      ...Platform.select({
        default: {
          justifyContent: 'space-between',
        },
        android: {
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginTop: 4,
        },
      }),
    },
    footerVertical: {
      flexDirection: 'column',
    },
    buttonSeparatorHorizontal: {
      height: '100%',
      backgroundColor: PlatformColor('separator'), //"#A9ADAE",
      width: StyleSheet.hairlineWidth,
    },
    buttonSeparatorVertical: {
      width: '100%',
      backgroundColor: PlatformColor('separator'), //"#A9ADAE",
      height: StyleSheet.hairlineWidth,
    },
  });

export default DialogContainer;
